/**
 * File Parser Utilities
 * Extract text content from various file types (PDF, DOCX, plain text)
 * Also supports PDF to images conversion for vision models
 */

import mammoth from 'mammoth';
import { Logger } from '../logger';

const log = Logger.create('fileParser');

// PDF.js version for CDN loading
const PDFJS_VERSION = '3.11.174';
let pdfjsLib = null;

/**
 * Dynamically load PDF.js from CDN (avoids bundling issues)
 */
async function loadPdfJs() {
  if (pdfjsLib) return pdfjsLib;
  
  // Load PDF.js script dynamically
  return new Promise((resolve, reject) => {
    if (window.pdfjsLib) {
      pdfjsLib = window.pdfjsLib;
      pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;
      resolve(pdfjsLib);
      return;
    }
    
    const script = document.createElement('script');
    script.src = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.min.js`;
    script.onload = () => {
      pdfjsLib = window.pdfjsLib;
      pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}/pdf.worker.min.js`;
      resolve(pdfjsLib);
    };
    script.onerror = () => reject(new Error('PDF.js konnte nicht geladen werden'));
    document.head.appendChild(script);
  });
}

/**
 * Image quality presets for PDF to image conversion
 */
export const IMAGE_QUALITY_PRESETS = {
  low: { scale: 1.0, quality: 0.7, description: 'Niedrig (schnell, weniger Tokens)' },
  medium: { scale: 1.5, quality: 0.85, description: 'Mittel (ausgewogen)' },
  high: { scale: 2.0, quality: 0.95, description: 'Hoch (beste Qualität, mehr Tokens)' }
};

/**
 * Convert a single PDF page to a base64 image
 * @param {Object} page - PDF.js page object
 * @param {number} scale - Scale factor for rendering (higher = better quality but larger)
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<string>} Base64 encoded image (without data URL prefix)
 */
async function renderPageToImage(page, scale = 1.5, quality = 0.85) {
  const viewport = page.getViewport({ scale });
  
  // Create canvas
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.height = viewport.height;
  canvas.width = viewport.width;
  
  // Render page to canvas
  const renderContext = {
    canvasContext: context,
    viewport: viewport
  };
  
  await page.render(renderContext).promise;
  
  // Convert to base64 JPEG (smaller than PNG)
  const dataUrl = canvas.toDataURL('image/jpeg', quality);
  
  // Return just the base64 part (without "data:image/jpeg;base64," prefix)
  return dataUrl.split(',')[1];
}

/**
 * Convert entire PDF file to array of base64 images
 * @param {File} file - PDF file
 * @param {Object} options - Conversion options
 * @param {string} options.quality - Quality preset: 'low', 'medium', 'high'
 * @param {number[]} options.pageNumbers - Optional array of specific page numbers (1-based)
 * @param {function} options.onProgress - Optional progress callback (pageNum, totalPages)
 * @returns {Promise<Object>} Object with images array and metadata
 */
export async function convertPdfToImages(file, options = {}) {
  const { 
    quality = 'medium', 
    pageNumbers = null,
    onProgress = null 
  } = options;
  
  const preset = IMAGE_QUALITY_PRESETS[quality] || IMAGE_QUALITY_PRESETS.medium;
  
  try {
    const pdfjs = await loadPdfJs();
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    
    const images = [];
    const pagesToProcess = pageNumbers || Array.from({ length: pdf.numPages }, (_, i) => i + 1);
    
    for (let i = 0; i < pagesToProcess.length; i++) {
      const pageNum = pagesToProcess[i];
      
      if (pageNum < 1 || pageNum > pdf.numPages) {
        log.warn(`Skipping invalid page number: ${pageNum}`);
        continue;
      }
      
      const page = await pdf.getPage(pageNum);
      const base64Image = await renderPageToImage(page, preset.scale, preset.quality);
      
      images.push({
        pageNumber: pageNum,
        base64: base64Image,
        mimeType: 'image/jpeg'
      });
      
      if (onProgress) {
        onProgress(i + 1, pagesToProcess.length);
      }
    }
    
    return {
      fileName: file.name,
      totalPages: pdf.numPages,
      processedPages: images.length,
      quality: quality,
      images: images
    };
  } catch (error) {
    throw new Error(`PDF zu Bilder Konvertierung fehlgeschlagen: ${error.message}`);
  }
}

/**
 * Estimate token cost for sending images to vision models
 * Based on approximate token costs (varies by provider)
 * @param {number} imageCount - Number of images
 * @param {string} quality - Quality preset
 * @returns {Object} Estimated token costs
 */
export function estimateImageTokens(imageCount, quality = 'medium') {
  // Approximate tokens per image based on quality (these are rough estimates)
  const tokensPerImage = {
    low: 85,      // ~512x512 low detail
    medium: 170,  // ~768x768 medium detail
    high: 765     // ~2048x2048 high detail
  };
  
  const tokens = tokensPerImage[quality] || tokensPerImage.medium;
  const total = imageCount * tokens;
  
  return {
    perImage: tokens,
    total: total,
    warning: total > 10000 ? 'Hoher Token-Verbrauch! Erwäge niedrigere Qualität.' : null
  };
}

/**
 * Extract text from PDF file
 */
async function extractPdfText(file) {
  try {
    const pdfjs = await loadPdfJs();
    const arrayBuffer = await file.arrayBuffer();
    const loadingTask = pdfjs.getDocument({ data: arrayBuffer });
    const pdf = await loadingTask.promise;
    
    let fullText = '';
    
    // Extract text from each page
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const textContent = await page.getTextContent();
      const pageText = textContent.items.map(item => item.str).join(' ');
      fullText += pageText + '\n\n';
    }
    
    return fullText.trim();
  } catch (error) {
    throw new Error(`PDF-Parsing fehlgeschlagen: ${error.message}`);
  }
}

/**
 * Extract text from DOCX file
 */
async function extractDocxText(file) {
  try {
    const arrayBuffer = await file.arrayBuffer();
    const result = await mammoth.extractRawText({ arrayBuffer });
    
    if (result.messages.length > 0) {
      log.warn('DOCX parsing warnings:', result.messages);
    }
    
    return result.value;
  } catch (error) {
    throw new Error(`DOCX-Parsing fehlgeschlagen: ${error.message}`);
  }
}

/**
 * Read plain text file
 */
async function readTextFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = (error) => reject(new Error(`Text-Datei konnte nicht gelesen werden: ${error}`));
    reader.readAsText(file);
  });
}

/**
 * Detect file type and extract text accordingly
 */
export async function parseFile(file) {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();
  
  // PDF files
  if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
    const text = await extractPdfText(file);
    return {
      name: file.name,
      type: 'pdf',
      content: text,
      size: file.size
    };
  }
  
  // DOCX files
  if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || 
      fileName.endsWith('.docx')) {
    const text = await extractDocxText(file);
    return {
      name: file.name,
      type: 'docx',
      content: text,
      size: file.size
    };
  }
  
  // Text-based files (code, markdown, plain text, etc.)
  const textExtensions = ['.txt', '.js', '.jsx', '.ts', '.tsx', '.py', '.java', '.cpp', 
                         '.c', '.h', '.cs', '.rb', '.go', '.rs', '.swift', '.kt',
                         '.php', '.html', '.css', '.scss', '.json', '.xml', '.yaml',
                         '.yml', '.md', '.sql', '.sh', '.bash'];
  
  const isTextFile = textExtensions.some(ext => fileName.endsWith(ext)) ||
                     fileType.startsWith('text/') ||
                     fileType === 'application/json' ||
                     fileType === 'application/javascript';
  
  if (isTextFile) {
    const text = await readTextFile(file);
    return {
      name: file.name,
      type: 'text',
      content: text,
      size: file.size
    };
  }
  
  // Unsupported file type
  throw new Error(`Dateityp nicht unterstützt: ${file.name}. Unterstützte Formate: PDF, DOCX, TXT, und Code-Dateien.`);
}

/**
 * Parse multiple files
 */
export async function parseFiles(files) {
  const results = [];
  const errors = [];
  
  for (const file of files) {
    try {
      const parsed = await parseFile(file);
      results.push(parsed);
    } catch (error) {
      errors.push({ file: file.name, error: error.message });
    }
  }
  
  return { results, errors };
}

/**
 * Check if file size is within limits
 */
export function validateFileSize(file, maxSizeMB = 10) {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    throw new Error(`Datei "${file.name}" ist zu groß (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum: ${maxSizeMB}MB`);
  }
  return true;
}

/**
 * Get file extension
 */
export function getFileExtension(fileName) {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
  return (bytes / 1024 / 1024).toFixed(2) + ' MB';
}
